/** The analysis and dashboard pages' shared pieces: their icons (24-unit
 *  stroke paths) and the modal dialog they open forms in. */
import { useEffect, useRef, type ReactNode, type RefObject } from 'react'

export const ICONS = {
  plus: 'M12 5v14M5 12h14', search: 'M20 20l-4-4M17 10a7 7 0 1 1-14 0 7 7 0 0 1 14 0',
  grid: 'M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z',
  list: 'M8 5h13M8 12h13M8 19h13M3 5h.01M3 12h.01M3 19h.01',
  star: 'm12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2-5.6-3-5.6 3 1.1-6.2L3 9.6l6.2-.9z',
  chart: 'M3 3v18h18M6 15l4-5 4 3 6-7',
  scatter: 'M3 3v18h18M7 15h.1M10 11h.1M12 14h.1M15 8h.1M18 6h.1M18 10h.1',
  table: 'M3 4h18v16H3zM3 9h18M3 14h18M9 4v16M15 4v16',
  arrow: 'm10 5-7 7 7 7M3 12h18', close: 'm6 6 12 12M6 18 18 6',
  settings: 'M4 7h6M14 7h6M4 17h10M18 17h2M10 4v6M14 14v6',
  sources: 'M3 4h18v16H3zM9 4v16M5 8h2M5 12h2M5 16h2',
  download: 'M12 3v12m-5-5 5 5 5-5M4 16v5h16v-5',
  upload: 'M12 16V4m-5 5 5-5 5 5M4 16v5h16v-5',
  copy: 'M8 8h13v13H8zM16 8V3H3v13h5',
  trash: 'M3 6h18M9 6V3h6v3M6 6l1 15h10l1-15M10 10v7M14 10v7',
  refresh: 'M20 7v5h-5M4 17v-5h5M5.5 7a7 7 0 0 1 11.6-2L20 8M4 16l2.9 3A7 7 0 0 0 18.5 17',
  expand: 'M8 3H3v5M16 3h5v5M3 16v5h5M21 16v5h-5',
  chevron: 'm8 5 7 7-7 7', pencil: 'm14 5 5 5M4 15 15 4a2.1 2.1 0 0 1 3 3L7 18l-4 1z',
  save: 'M4 3h13l4 4v14H3V3zM7 3v6h10V3M7 21v-8h10v8',
  people: 'M16 19v-1a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v1M9 10a3 3 0 1 0 0-6 3 3 0 0 0 0 6M22 19v-1a4 4 0 0 0-3-3.9M16 4.1a3 3 0 0 1 0 5.8',
  person: 'M19 20v-1a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v1M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8',
  bar: 'M3 3v18h18M7 17v-6M12 17V7M17 17v-9',
  wind: 'M3 8h11a3 3 0 1 0-3-3M3 12h16a3 3 0 1 1-3 3M3 16h7',
  duration: 'M3 3v18h18M5 6c4 0 6 3 8 7s5 6 8 6',
  rating: 'M3 3v18h18M6 18c3-1 5-4 7-8s4-6 7-7',
  completeness: 'M3 4h18v17H3zM3 9h18M8 2v4M16 2v4M7 13h2M11 13h2M15 13h2M7 17h2',
  dashboard: 'M3 3h8v10H3zM13 3h8v6h-8zM13 11h8v10h-8zM3 15h8v6H3z',
  split: 'M3 4h18v7H3zM3 13h18v7H3z',
  up: 'm6 15 6-6 6 6', down: 'm6 9 6 6 6-6',
  image: 'M3 4h18v16H3zM8 10a2 2 0 1 0 0-.1M21 16l-5-5-9 9',
  text: 'M4 6h16M4 12h16M4 18h10',
} as const
export type IconName = keyof typeof ICONS
export function Icon({ name }: { name: IconName }) {
  return <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.65"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={ICONS[name]} /></svg>
}

export function Dialog({ title, onClose, children, className = '', id, label = title, badge, closeLabel = 'Close dialog', returnFocusRef }: {
  title: string; onClose: () => void; children: ReactNode; className?: string; id?: string; label?: string;
  badge?: ReactNode; closeLabel?: string; returnFocusRef?: RefObject<HTMLButtonElement>;
}) {
  const ref = useRef<HTMLDialogElement>(null)
  const backdropPress = useRef(false)
  useEffect(() => {
    const dialog = ref.current
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    dialog?.showModal()
    return () => {
      dialog?.close()
      document.body.style.overflow = previousOverflow
      const target = returnFocusRef?.current ?? previousFocus
      if (target?.isConnected) target.focus({ preventScroll: true })
    }
  }, [returnFocusRef])
  const outside = (event: { currentTarget: HTMLDialogElement; clientX: number; clientY: number }) => {
    const bounds = event.currentTarget.getBoundingClientRect()
    return event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom
  }
  return <dialog ref={ref} id={id} className={`analysis-dialog ${className}`} aria-label={label}
    onCancel={event => { event.preventDefault(); onClose() }}
    onPointerDown={event => { backdropPress.current = event.target === event.currentTarget && outside(event) }}
    onClick={event => { if (backdropPress.current && event.target === event.currentTarget && outside(event)) onClose(); backdropPress.current = false }}>
    <header><div className="analysis-dialog-title"><h2>{title}</h2>{badge}</div><button type="button" className="analysis-icon-button" aria-label={closeLabel} onClick={onClose}><Icon name="close" /></button></header>
    {children}
  </dialog>
}

